import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService, SortEvent } from 'primeng/api';
import { Subscription } from 'rxjs';
import { PessoaService } from '../../../services/pessoas/pessoas.service';
import { TableColumn } from '../../../models/shared/table-column.model';

@Component({
  selector: 'app-lista-de-clientes',
  templateUrl: './lista-de-clientes.component.html',
  styleUrls: ['./lista-de-clientes.component.scss'],
  providers: [MessageService],
})
export class ListaDeClientesComponent implements OnInit, OnDestroy {
  deleteDialogVisible = false;
  deleteItemId: string | null = null;

  // Objetos de traducao (padrao nova-despesa)
  texts = {
    titulo: '',
    subtitulo: '',
    lista_de_clientes: '',
    nenhum_cliente: '',
    ajuste_filtros: '',
    carregando: '',
    confirmar_exclusao: '',
    confirmacao: '',
    cliente_excluido_sucesso: '',
    erro_ao_excluir_cliente: ''
  };

  labels = {
    codigo_cliente: '',
    nome: '',
    nome_fantasia: '',
    telefone: '',
    ci: '',
    ruc: '',
    cidade: '',
    data_inclusao: '',
    acoes: '',
    itens_por_pagina: ''
  };

  placeholders = {
    pesquisar: ''
  };

  botoes = {
    adicionar_cliente: '',
    confirmar: '',
    cancelar: ''
  };

  tooltips = {
    visualizar: '',
    editar: '',
    excluir: ''
  };

  private langChangeSub!: Subscription;

  // Dados da tabela
  pessoas: any[] = [];
  first3: number = 0;
  rows3: number = 10;
  totalRecords: number = 0;
  loading: boolean = true;
  searchNome: string = '';
  lastfield = 'id';
  lastSort = 'asc';

  pageSizeOptions = [
    { label: 5, value: 5 },
    { label: 10, value: 10 },
    { label: 20, value: 20 },
    { label: 120, value: 120 },
  ];

  // Configuracao de colunas visiveis (padrao nova-despesa)
  readonly STORAGE_KEY = 'lista-de-clientes-visible-columns';

  columns: TableColumn[] = [
    { key: 'codigoPessoa', labelKey: 'lista-de-clientes.labels.codigo_cliente', visible: true, fixed: true, defaultVisible: true },
    { key: 'nome', labelKey: 'lista-de-clientes.labels.nome', visible: true, fixed: false, defaultVisible: true },
    { key: 'fantasia', labelKey: 'lista-de-clientes.labels.nome_fantasia', visible: true, fixed: false, defaultVisible: true },
    { key: 'telefone', labelKey: 'lista-de-clientes.labels.telefone', visible: true, fixed: false, defaultVisible: true },
    { key: 'ci', labelKey: 'lista-de-clientes.labels.ci', visible: true, fixed: false, defaultVisible: true },
    { key: 'ruc', labelKey: 'lista-de-clientes.labels.ruc', visible: true, fixed: false, defaultVisible: true },
    { key: 'cidade', labelKey: 'lista-de-clientes.labels.cidade', visible: true, fixed: false, defaultVisible: true },
    { key: 'criadoEm', labelKey: 'lista-de-clientes.labels.data_inclusao', visible: true, fixed: false, defaultVisible: true },
    { key: 'acoes', labelKey: 'lista-de-clientes.labels.acoes', visible: true, fixed: true, defaultVisible: true },
  ];

  constructor(
    private messageService: MessageService,
    private pessoaService: PessoaService,
    private router: Router,
    private translate: TranslateService
  ) { }

  ngOnInit() {
    this.loadColumnPreferences();
    this.findClientes();
    this.loadTranslations();

    // Atualiza traducoes quando idioma muda
    this.langChangeSub = this.translate.onLangChange.subscribe(() => {
      this.loadTranslations();
    });
  }

  ngOnDestroy(): void {
    if (this.langChangeSub) {
      this.langChangeSub.unsubscribe();
    }
  }

  private loadTranslations(): void {
    this.texts = {
      titulo: this.translate.instant('lista-de-clientes.texts.titulo'),
      subtitulo: this.translate.instant('lista-de-clientes.texts.subtitulo'),
      lista_de_clientes: this.translate.instant('lista-de-clientes.texts.lista_de_clientes'),
      nenhum_cliente: this.translate.instant('lista-de-clientes.texts.nenhum_cliente'),
      ajuste_filtros: this.translate.instant('lista-de-clientes.texts.ajuste_filtros'),
      carregando: this.translate.instant('lista-de-clientes.texts.carregando'),
      confirmar_exclusao: this.translate.instant('lista-de-clientes.texts.confirmar_exclusao'),
      confirmacao: this.translate.instant('lista-de-clientes.texts.confirmacao'),
      cliente_excluido_sucesso: this.translate.instant('lista-de-clientes.texts.cliente_excluido_sucesso'),
      erro_ao_excluir_cliente: this.translate.instant('lista-de-clientes.texts.erro_ao_excluir_cliente')
    };

    this.labels = {
      codigo_cliente: this.translate.instant('lista-de-clientes.labels.codigo_cliente'),
      nome: this.translate.instant('lista-de-clientes.labels.nome'),
      nome_fantasia: this.translate.instant('lista-de-clientes.labels.nome_fantasia'),
      telefone: this.translate.instant('lista-de-clientes.labels.telefone'),
      ci: this.translate.instant('lista-de-clientes.labels.ci'),
      ruc: this.translate.instant('lista-de-clientes.labels.ruc'),
      cidade: this.translate.instant('lista-de-clientes.labels.cidade'),
      data_inclusao: this.translate.instant('lista-de-clientes.labels.data_inclusao'),
      acoes: this.translate.instant('lista-de-clientes.labels.acoes'),
      itens_por_pagina: this.translate.instant('lista-de-clientes.labels.itens_por_pagina')
    };

    this.placeholders = {
      pesquisar: this.translate.instant('lista-de-clientes.placeholders.pesquisar')
    };

    this.botoes = {
      adicionar_cliente: this.translate.instant('lista-de-clientes.botoes.adicionar_cliente'),
      confirmar: this.translate.instant('lista-de-clientes.botoes.confirmar'),
      cancelar: this.translate.instant('lista-de-clientes.botoes.cancelar')
    };

    this.tooltips = {
      visualizar: this.translate.instant('lista-de-clientes.tooltips.visualizar'),
      editar: this.translate.instant('lista-de-clientes.tooltips.editar'),
      excluir: this.translate.instant('lista-de-clientes.tooltips.excluir')
    };
  }

  /** Verifica se uma coluna esta visivel */
  isColumnVisible(key: string): boolean {
    const col = this.columns.find(c => c.key === key);
    return col ? col.visible : true;
  }

  /** Quantidade de colunas visiveis (para colspan) */
  get visibleColumnCount(): number {
    return this.columns.filter(c => c.visible).length;
  }

  /** Handler para mudanca de colunas vindas do ColumnSelector */
  onColumnsChange(columns: TableColumn[]): void {
    this.columns = [...columns];
  }

  /** Carrega preferencias de colunas do localStorage */
  private loadColumnPreferences(): void {
    try {
      const stored = localStorage.getItem(this.STORAGE_KEY);
      if (stored) {
        const visibleKeys = JSON.parse(stored) as string[];
        this.columns.forEach(col => {
          if (!col.fixed) {
            col.visible = visibleKeys.includes(col.key);
          }
        });
      }
    } catch (e) {
      console.warn('Erro ao carregar preferencias de colunas:', e);
    }
  }

  findClientes(
    page: number = 0,
    size: number = this.rows3,
    sort: string = 'id,asc',
  ) {
    this.loading = true;
    let filter = `categoriaCliente=true`;

    if (this.searchNome && this.searchNome.trim() !== '') {
      const search = encodeURIComponent(this.searchNome.trim());

      filter += `&email=${search}`;
      filter += `&nome=${search}`;
      filter += `&fantasia=${search}`;
      filter += `&ruc=${search}`;
      filter += `&telefone=${search}`;
      filter += `&ci=${search}`;
      filter += `&nomeCidade=${search}`;

      if (!isNaN(Number(search))) {
        filter += `&codigoPessoa=${search}`;
      }
    }

    this.pessoaService
      .get('/pessoas/buscar', {
        page,
        size,
        sort,
        filter: filter,
      }).subscribe({
        next: (response) => {
          this.pessoas = response.content;
          this.totalRecords = response.totalElements;
          this.first3 = page * size;
          this.loading = false;
        },
        error: (error) => {
          console.error('Erro ao buscar clientes:', error);
          this.pessoas = [];
          this.totalRecords = 0;
          this.loading = false;
        }
      });
  }

  onPageChange3(event: any) {
    const page = event.page;
    const size = event.rows;
    this.findClientes(page, size);
  }

  onRowsPerPageChange() {
    this.first3 = 0;
    this.findClientes(0, this.rows3, `${this.lastfield},${this.lastSort}`);
  }

  customSort(event: SortEvent) {
    if (!event.field || !event.data) return;

    const field = event.field;
    const order = event.order ?? 1;

    // Atualiza lastfield/lastSort para manter ordenacao em buscas futuras
    this.lastfield = field;
    this.lastSort = order === 1 ? 'asc' : 'desc';

    event.data.sort((a: any, b: any) => {
      const valueA = this.getSortValue(a, field);
      const valueB = this.getSortValue(b, field);

      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return -1 * order;
      if (valueB == null) return 1 * order;

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.localeCompare(valueB) * order;
      }

      return (valueA < valueB ? -1 : valueA > valueB ? 1 : 0) * order;
    });
  }

  private getSortValue(item: any, field: string): any {
    const parts = field.split('.');
    let value: any = item;
    for (const part of parts) {
      if (value == null) return null;
      value = value[part];
    }
    if (value != null && typeof value === 'object' && 'nome' in value) {
      return value.nome;
    }
    return value;
  }

  onSearchChange(value: string) {
    this.searchNome = value || '';
    if (this.searchNome.length === 0 || this.searchNome.length >= 3) {
      this.search();
    }
  }

  editarCliente(id: string) {
    this.router.navigate(['/cliente/edit', id]);
  }

  deletarCliente(id: string) {
    this.deleteItemId = id;
    this.deleteDialogVisible = true;
  }

  confirmarExclusao() {
    if (!this.deleteItemId) return;
    this.pessoaService.delete(`/pessoas/${this.deleteItemId}`).subscribe({
      next: () => {
        this.messageService.add({
          severity: 'success',
          summary: this.texts.confirmacao,
          detail: this.texts.cliente_excluido_sucesso,
        });
        this.search();
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
      error: () => {
        this.messageService.add({
          severity: 'error',
          summary: this.texts.confirmacao,
          detail: this.texts.erro_ao_excluir_cliente,
        });
        this.deleteDialogVisible = false;
        this.deleteItemId = null;
      },
    });
  }

  search() {
    this.findClientes(
      0,
      this.rows3,
      `${this.lastfield},${this.lastSort}`
    );
  }

  viewCliente(id: string) {
    this.router.navigate(['/lista-de-clientes/view', id]);
  }

  formatarDataEHora(dataISO: string): string {
    const data = new Date(dataISO);
    if (isNaN(data.getTime())) {
      return '';
    }
    const dia = String(data.getDate()).padStart(2, '0');
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const ano = data.getFullYear();
    const horas = String(data.getHours()).padStart(2, '0');
    const minutos = String(data.getMinutes()).padStart(2, '0');
    return `${dia}/${mes}/${ano} ${horas}:${minutos}`;
  }
}
